/** Ordering the rows of a `GridTable` the caller holds - the one hook the table pages share.
 *
 * `GridTable` only *reports* a sort (a client sort of one page would order a page and present it as
 * the list), so a screen that holds its whole list keeps the state and orders it here. The ordering
 * is `sortRows`, the one the rest of the product uses: numbers as numbers, text naturally, empty last.
 * A screen that is server-paged does not use this; it refetches on `onSortChange`.
 */

import { useMemo, useState } from "react";

import type { SortState } from "../components/ui";
import { sortRows } from "../components/ui";

type Key = string | number | null | undefined;

export function useGridSort<T>(
  rows: readonly T[],
  accessors: Readonly<Record<string, (row: T) => Key>>,
) {
  const [sort, setSort] = useState<SortState | null>(null);
  const sorted = useMemo(
    () => (sort ? sortRows(rows, accessors[sort.columnId], sort.direction) : [...rows]),
    // The accessors are a literal in the caller; the rows and the sort are what change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, sort],
  );
  return { sorted, sort, setSort };
}
